import type { MapFeatureHoverCardProps } from "../../contracts/map-assets";
import { cn } from "../../lib/utils";

/**
 * A compact, overlay-friendly card for transient feature hover summaries.
 * Optimized for quick scanning of key attributes without obstructing too much map area.
 */
export function MapFeatureHoverCard({
  title,
  subtitle,
  badges = [],
  summary = [],
  className,
}: MapFeatureHoverCardProps) {
  return (
    <div
      className={cn(
        "flex flex-col w-56 bg-background/95 backdrop-blur-sm border border-border rounded shadow-lg overflow-hidden select-none animate-in fade-in zoom-in duration-100",
        className
      )}
    >
      {/* Header */}
      <div className="flex flex-col p-2.5 bg-muted/5 border-b border-border/50">
        <h4 className="text-[10px] font-black leading-tight truncate uppercase tracking-tight text-foreground">
          {title}
        </h4>
        {subtitle && (
          <p className="text-[9px] text-muted-foreground leading-tight truncate mt-0.5">
            {subtitle}
          </p>
        )}
      </div>

      {/* Content */}
      <div className="flex flex-col p-2.5 gap-2">
        {/* Badges */}
        {badges.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {badges.map((badge, i) => (
              <span
                key={i}
                className="px-1.5 py-0.5 rounded-[2px] bg-primary/10 text-primary text-[8px] font-black uppercase tracking-widest border border-primary/10"
              >
                {badge}
              </span>
            ))}
          </div>
        )}

        {/* Summary Rows */}
        {summary.length > 0 && (
          <div className="flex flex-col gap-1">
            {summary.map((item) => (
              <div key={item.key} className="flex items-center justify-between gap-2">
                <span className="text-[9px] text-muted-foreground font-bold truncate uppercase tracking-tighter">
                  {item.label}
                </span>
                <span className="text-[9px] font-mono font-bold text-foreground shrink-0 tabular-nums">
                  {item.value}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Subtle Bottom Accent */}
      <div className="h-0.5 w-full bg-primary/20" />
    </div>
  );
}
